import React, { forwardRef } from 'react'
import { cn } from '@utils/cn'
import { checkForOnlyPositiveNumbers } from '@/helpers'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  required?: boolean
  maxLength?: number
  showCharacterCount?: boolean;
  onlyNumber09?: boolean;
  labelClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      required = false,
      maxLength,
      showCharacterCount = false,
      id,
      onlyNumber09 = false,
      onChange,
      labelClassName,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
    const hasError = !!error
    const currentLength = props.value?.toString().length || 0

    const baseClasses = 'flex h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900 dark:placeholder:text-gray-400'
    
    const stateClasses = {
      default: 'border-gray-300 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400',
      error: 'border-error-500 focus-visible:ring-error-500 dark:border-error-400 dark:focus-visible:ring-error-400',
      success: 'border-success-500 focus-visible:ring-success-500 dark:border-success-400 dark:focus-visible:ring-success-400'
    }

    const widthClasses = fullWidth ? 'w-full' : ''
    
    const getStateClass = () => {
      if (hasError) return stateClasses.error
      if (props.value && !hasError) return stateClasses.success
      return stateClasses.default
    }

    return (
      <div className={cn('space-y-2', widthClasses)}>
        {label && (
          <label 
            htmlFor={inputId}
            className={
              cn(
                'block text-sm font-medium text-gray-700 dark:text-gray-300',
                labelClassName
              )
            }
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-800">
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            onChange={(e)=>{
              if(!onlyNumber09){
                onChange && onChange(e);
                return;
              }
              else{
              const val= checkForOnlyPositiveNumbers(e.target.value)

              if (val) {
                onChange && onChange(e);
              }
              }
            }}
            className={cn(
              baseClasses,
              getStateClass(),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError 
                ? `${inputId}-error` 
                : helperText 
                  ? `${inputId}-helper` 
                  : undefined
            }
            maxLength={maxLength}
            {...props}
          />
          
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        
        {(hasError || helperText || (showCharacterCount && maxLength)) && (
          <div className="flex items-center justify-between text-xs">
            <div className="space-y-1">
              {hasError && (
                <p 
                  id={`${inputId}-error`}
                  className="text-error-600 dark:text-error-400"
                  role="alert"
                >
                  {error}
                </p>
              )}
              {helperText && !hasError && (
                <p 
                  id={`${inputId}-helper`}
                  className="text-gray-500 dark:text-gray-400"
                >
                  {helperText}
                </p>
              )}
            </div>
            
            {showCharacterCount && maxLength && (
              <span className="text-gray-400">
                {currentLength}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input 